import { useState } from 'react';
import Sidebar from '../components/Sidebar';
import { useLanguage } from '../lib/language';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ChevronDown, ChevronRight, Camera, Image, Users, ShoppingCart, Calendar, FileText, Cloud, Palette, MessageSquare, Heart, Scissors, BookOpen, Trophy } from 'lucide-react';

const sections = [
  {
    id: 'galleries',
    icon: Image,
    title: { pl: 'Galerie', en: 'Galleries' },
    content: {
      pl: `**Tworzenie galerii**
1. Przejdz do Galerie → Nowa galeria
2. Podaj nazwe, opis, date wydarzenia
3. Wybierz motyw kolorystyczny, layout (masonry/grid/slideshow), dostep (publiczny/haslo/token)
4. Wlacz watermark jesli chcesz chronic zdjecia

**Upload zdjec**
- Przeciagnij zdjecia (drag & drop) do galerii lub kliknij Upload
- Mozesz importowac z pliku ZIP
- Zdjecia automatycznie otrzymuja miniaturki i watermarki

**Udostepnianie**
- Skopiuj link publiczny lub wygeneruj kod QR
- Wyslij klientowi mailem bezposrednio z aplikacji`,
      en: `**Creating galleries**
1. Go to Galleries → New Gallery
2. Enter name, description, event date
3. Choose theme, layout (masonry/grid/slideshow), access (public/password/token)
4. Enable watermark to protect photos

**Uploading photos**
- Drag & drop photos into gallery or click Upload
- You can import from ZIP files
- Photos automatically get thumbnails and watermarks

**Sharing**
- Copy public link or generate QR code
- Send to client via email directly from app`
    }
  },
  {
    id: 'proofing',
    icon: Heart,
    title: { pl: 'Proofing - wybor zdjec', en: 'Proofing - photo selection' },
    content: {
      pl: `**Jak to dziala:**
1. Klient otwiera galerie przez publiczny link
2. Oznacza zdjecia kolorami:
   - Zielony = zatwierdzony
   - Zolty = do zastanowienia
   - Czerwony = odrzucony
3. Zatwierdza swoj wybor

**Co widzisz jako fotograf:**
- Galerie → wybrana galeria → zakladka Selekcje
- Lista wyborow klienta z kolorami
- Mozesz zaakceptowac, odrzucic lub skomentowac`,
      en: `**How it works:**
1. Client opens gallery via public link
2. Marks photos with colors:
   - Green = approved
   - Yellow = maybe
   - Red = rejected
3. Submits their selection

**What you see as photographer:**
- Galleries → selected gallery → Selections tab
- Client's choices with color labels
- You can accept, reject or comment`
    }
  },
  {
    id: 'clients',
    icon: Users,
    title: { pl: 'Klienci', en: 'Clients' },
    content: {
      pl: `**Zarzadzanie klientami:**
- Lista klientow z historia zamowien, selekcji i platnosci
- Kliknij klienta aby zobaczyc pelny profil
- Tagi pomagaja organizowac klientow (slub, portret, event, VIP)

**Wiadomosci:**
- Czat z klientem zintegrowany z mailem (SMTP)
- Klient odpowiada mailem lub w galerii`,
      en: `**Managing clients:**
- Client list with order history, selections and payments
- Click client for full profile
- Tags help organize clients (wedding, portrait, event, VIP)

**Messages:**
- Chat with client integrated with email (SMTP)
- Client responds via email or in gallery`
    }
  },
  {
    id: 'bookings',
    icon: Calendar,
    title: { pl: 'Rezerwacje', en: 'Bookings' },
    content: {
      pl: `**Konfiguracja:**
1. Przejdz do Rezerwacje
2. Dodaj uslugi (np. "Sesja portretowa - 2h - 500 PLN")
3. Ustaw dostepnosc w kalendarzu

**Jak klient rezerwuje:**
1. Wchodzi na Twoja publiczna strone rezerwacji
2. Wybiera usluge, date i godzine
3. Placi zaliczke przez Stripe
4. Oboje otrzymujecie potwierdzenie mailem`,
      en: `**Setup:**
1. Go to Bookings
2. Add services (e.g., "Portrait session - 2h - 500 PLN")
3. Set availability in calendar

**How client books:**
1. Visits your public booking page
2. Selects service, date and time
3. Pays deposit via Stripe
4. Both receive email confirmation`
    }
  },
  {
    id: 'orders',
    icon: ShoppingCart,
    title: { pl: 'Zamowienia i platnosci', en: 'Orders & payments' },
    content: {
      pl: `**Tworzenie zamowienia:**
1. Zamowienia → Nowe zamowienie
2. Wybierz klienta i galerie
3. Dodaj pozycje (zdjecia, odbitki, albumy) z cenami
4. Wyslij link do platnosci

**Platnosci:** Stripe checkout - karta lub Przelewy24
**Faktury:** Automatycznie generowane, dostepne w zakladce Faktury`,
      en: `**Creating order:**
1. Orders → New order
2. Select client and gallery
3. Add items (photos, prints, albums) with prices
4. Send payment link

**Payments:** Stripe checkout - card or Przelewy24
**Invoices:** Automatically generated, available in Invoices tab`
    }
  },
  {
    id: 'contracts',
    icon: FileText,
    title: { pl: 'Kontrakty', en: 'Contracts' },
    content: {
      pl: `1. Kontrakty → Nowy kontrakt
2. Wybierz szablon (sesja, slub, event)
3. Uzupelnij dane klienta, warunki, cene
4. Wyslij do klienta - podpisuje elektronicznie
5. Podpisany kontrakt dostepny jako PDF`,
      en: `1. Contracts → New contract
2. Choose template (session, wedding, event)
3. Fill client data, terms, price
4. Send to client - signs electronically
5. Signed contract available as PDF`
    }
  },
  {
    id: 'retouch',
    icon: Scissors,
    title: { pl: 'Retusz', en: 'Retouching' },
    content: {
      pl: `1. Klient zamawia retusz zdjecia w galerii
2. Zlecenie pojawia sie w Zlecenia retuszu
3. Edytor wykonuje retusz, uploaduje nowa wersje
4. Zdjecie ma historie wersji (before/after)`,
      en: `1. Client requests retouch of a photo in gallery
2. Request appears in Retouch Requests
3. Editor performs retouch, uploads new version
4. Photo has version history (before/after)`
    }
  },
  {
    id: 'cloud',
    icon: Cloud,
    title: { pl: 'Synchronizacja z chmura', en: 'Cloud sync' },
    content: {
      pl: `1. Cloud → Polacz konto (Google Drive, Dropbox lub OneDrive)
2. Wybierz folder zrodlowy
3. Sync dwukierunkowy - zdjecia w chmurze pojawiaja sie w galerii i odwrotnie`,
      en: `1. Cloud → Connect account (Google Drive, Dropbox or OneDrive)
2. Select source folder
3. Two-way sync - cloud photos appear in gallery and vice versa`
    }
  },
  {
    id: 'albums',
    icon: BookOpen,
    title: { pl: 'Projektant albumow', en: 'Album designer' },
    content: {
      pl: `1. Albumy → Nowy album
2. Wybierz szablon (rozmiar, styl)
3. Przeciagnij zdjecia na strony (drag & drop)
4. Ustaw uklad, tla, teksty
5. Eksportuj jako PDF lub JPG do druku`,
      en: `1. Albums → New album
2. Choose template (size, style)
3. Drag photos onto pages
4. Set layout, backgrounds, text
5. Export as PDF or JPG for print`
    }
  },
  {
    id: 'wedding',
    icon: Heart,
    title: { pl: 'Modul slubny', en: 'Wedding module' },
    content: {
      pl: `1. Sluby → Nowe wydarzenie slubne
2. Ustaw budzet z kategoriami wydatkow
3. Dodaj liste gosci z danymi kontaktowymi
4. Goscie potwierdzaja obecnosc (RSVP) online
5. Publiczna strona slubna dla gosci`,
      en: `1. Weddings → New wedding event
2. Set budget with expense categories
3. Add guest list with contact details
4. Guests confirm attendance (RSVP) online
5. Public wedding page for guests`
    }
  },
  {
    id: 'sport',
    icon: Trophy,
    title: { pl: 'Modul sportowy (Flyball)', en: 'Sport module (Flyball)' },
    content: {
      pl: `1. Sport → zakladki: Sezony, Wydarzenia, Zespoly, Psy
2. Przegladaj galerie z zawodow
3. AI automatycznie clusteruje zdjecia po psach
4. Koszyk i zamowienia zdjec z wydarzen sportowych`,
      en: `1. Sport → tabs: Seasons, Events, Teams, Dogs
2. Browse event galleries
3. AI automatically clusters photos by dogs
4. Cart and photo orders from sport events`
    }
  },
  {
    id: 'ai',
    icon: Camera,
    title: { pl: 'AI / Copilot', en: 'AI / Copilot' },
    content: {
      pl: `**Dostepne funkcje AI:**
- **Smart Delivery** - AI wybiera najlepsze zdjecia z sesji
- **Grupowanie twarzy** - automatyczne grupowanie zdjec po osobach
- **SEO** - generowanie opisow i tagow dla galerii
- **Analiza zdjec** - ocena jakosci, ostroci, ekspozycji`,
      en: `**Available AI features:**
- **Smart Delivery** - AI picks best photos from session
- **Face grouping** - automatic grouping by person
- **SEO** - generating descriptions and tags for galleries
- **Photo analysis** - quality, sharpness, exposure scoring`
    }
  },
  {
    id: 'settings',
    icon: Palette,
    title: { pl: 'Ustawienia', en: 'Settings' },
    content: {
      pl: `**Profil:** nazwa studia, subdomena, logo, kolory, czcionka
**Email:** konfiguracja SMTP do wysylki maili
**Platnosci:** Stripe - karta i Przelewy24
**Jezyk:** polski, angielski, niemiecki`,
      en: `**Profile:** studio name, subdomain, logo, colors, font
**Email:** SMTP configuration for sending emails
**Payments:** Stripe - card and Przelewy24
**Language:** Polish, English, German`
    }
  }
];

function Section({ section, language }) {
  const [open, setOpen] = useState(false);
  const Icon = section.icon;

  return (
    <div className="border-b border-border last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 py-4 px-4 hover:bg-secondary/30 transition text-left"
      >
        <Icon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        <span className="font-medium flex-1">{section.title[language] || section.title.pl}</span>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pl-12 text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
          {(section.content[language] || section.content.pl).split('**').map((part, i) =>
            i % 2 === 1 ? <strong key={i} className="text-foreground">{part}</strong> : part
          )}
        </div>
      )}
    </div>
  );
}

export default function HelpPage() {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/help" />
      <main className="lg:ml-64 p-6 lg:p-10">
        <h1 className="text-3xl font-bold mb-2">
          {language === 'pl' ? 'Pomoc' : 'Help'}
        </h1>
        <p className="text-muted-foreground mb-8">
          {language === 'pl' ? 'Przewodnik po funkcjach Focus Photo Pro' : 'Guide to Focus Photo Pro features'}
        </p>

        <Card className="bg-card border-border">
          <CardContent className="p-0">
            {sections.map(s => (
              <Section key={s.id} section={s} language={language} />
            ))}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
